import { Botao, Selo, SeloDeEmpresa } from "@plantus/ui";
import type { Coluna } from "@plantus/ui";
import type { FornecedorCadastrado, SituacaoDaQualificacao } from "@plantus/api-client";

/**
 * As colunas da lista de fornecedores, e a decisão de qualificação em cada
 * linha.
 *
 * <p>Saíram de dentro de `PaginaFornecedores.tsx` no recorte do ADR-0013 §1/§2
 * — o arquivo media 405 linhas contra um teto de 300, e o destino registrado no
 * ADR é literalmente <i>"lista · decisão de qualificação"</i>. Nenhuma definição
 * mudou de conteúdo: o que este arquivo é, é o mesmo `colunas()` de antes, no
 * endereço dele. Mesmo recorte, mesmo formato e mesmo motivo que
 * `PaginaLotesColunas.tsx`, da produção.
 *
 * <p>Fora do componente porque a definição é estável e a lista não: recriar o
 * arranjo a cada decisão registrada é trabalho por nada.
 */

const ROTULO: Record<SituacaoDaQualificacao, string> = {
  EM_QUALIFICACAO: "Em qualificação",
  QUALIFICADO: "Qualificado",
  REPROVADO: "Reprovado",
  SUSPENSO: "Suspenso",
};

/**
 * "Em qualificação" é âmbar e não vermelho: não é problema, é etapa. Vermelho
 * para o que está em andamento normal treina a pessoa a ignorar vermelho.
 */
const TOM: Record<SituacaoDaQualificacao, "vigente" | "vencendo" | "vencido" | "emObtencao"> = {
  EM_QUALIFICACAO: "emObtencao",
  QUALIFICADO: "vigente",
  REPROVADO: "vencido",
  SUSPENSO: "vencendo",
};

export type Decisao = (f: FornecedorCadastrado, acao: "qualificar" | "reprovar" | "suspender") => void;

/**
 * As colunas. O nome do fornecedor é o que identifica a linha — vira o título
 * do cartão no celular.
 */
export function colunas(decidir: Decisao, consolidado: boolean): Coluna<FornecedorCadastrado>[] {
  // A coluna de empresa só existe no consolidado, e é a segunda: logo depois
  // do que identifica a linha, antes da situação — porque é ela que diz DE
  // QUEM é aquela situação. Com uma empresa só, seria a mesma palavra repetida
  // em toda linha, e coluna que se repete é coluna que o olho aprende a pular.
  const empresa: Coluna<FornecedorCadastrado>[] = consolidado
    ? [
        {
          chave: "empresa",
          rotulo: "Empresa",
          // O nome vem do servidor, junto da linha. Junção feita aqui poderia
          // não encontrar a empresa e deixar o selo vazio — decisão de
          // qualificação sem CNPJ na tela.
          conteudo: (f) => <SeloDeEmpresa empresa={f.empresa} />,
        },
      ]
    : [];

  return [
    {
      chave: "nome",
      rotulo: "Fornecedor",
      principal: true,
      conteudo: (f) => (
        <span>
          {f.nome}
          {f.cnpj !== null && <small className="lotes__sub">{f.cnpj}</small>}
        </span>
      ),
    },
    ...empresa,
    {
      chave: "situacao",
      rotulo: "Situação",
      conteudo: (f) => <Selo situacao={TOM[f.situacao]}>{ROTULO[f.situacao]}</Selo>,
    },
    {
      chave: "desde",
      rotulo: "Desde",
      // Data do calendário, sem passar por fuso — e no formato que se lê no
      // Brasil. A API entrega ISO; ISO na tela é dado de máquina exposto.
      conteudo: (f) => (f.qualificadoEm === null ? "—" : formatar(f.qualificadoEm)),
    },
    { chave: "motivo", rotulo: "Motivo", conteudo: (f) => f.motivo ?? "—" },
    {
      chave: "decisao",
      rotulo: "Decisão",
      rotuloOculto: true,
      conteudo: (f) => (
        // ⚠️ "Reprovar" e "Suspender" RETIRAM a qualificação, e por isso não
        // podem sair na cor que aprova. `packages/ui` não tem
        // `aparencia="critica"` — as quatro são primaria · secundaria · texto ·
        // sobreMarca —, então quem pinta a tinta crítica é o contrato do botão
        // fantasma declarado por `.fornecedores__decisao` em `app.css`. É
        // correção por CONTEXTO, do mesmo tipo que o cabeçalho acabou de
        // abandonar: a próxima tela com uma ação de retirada não a herda.
        // Relatado como pendência de `packages/ui`.
        <span className="fornecedores__decisao">
          {f.situacao !== "QUALIFICADO" && (
            <Botao type="button" aparencia="secundaria" onClick={() => decidir(f, "qualificar")}>
              Qualificar
            </Botao>
          )}
          {f.situacao === "EM_QUALIFICACAO" && (
            <Botao type="button" aparencia="texto" onClick={() => decidir(f, "reprovar")}>
              Reprovar
            </Botao>
          )}
          {f.situacao === "QUALIFICADO" && (
            <Botao type="button" aparencia="texto" onClick={() => decidir(f, "suspender")}>
              Suspender
            </Botao>
          )}
        </span>
      ),
    },
  ];
}

/** Data do calendário, sem passar por fuso. Ver Etiqueta.tsx. */
function formatar(iso: string): string {
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}
